import { useCredentials } from '@ais-ui/auth';
import { useBasePath } from '@ais-ui/custom-hooks';
import { useSession } from '@ais-ui/websockets';
import { UseMutationResult, useQueryClient } from '@tanstack/react-query';

import {
  useTrackedMutation as useMutation,
  useTrackedQuery as useQuery,
} from '@ais-ui/performance-tracking';

import { useMemo } from 'react';
import { getBasePath } from './getAISBasePath';
import {
  Configuration,
  MessageApi,
  MessageSchedulePostRequest,
  MessageSendPostOperationRequest,
  type SchedulesResponse,
  type SentMessagesResponse,
} from '@ais-ui/api-spec/config';
import {
  convertFieldsToDates,
  getAuthHeaderFromCredentials,
} from '@ais-ui/utils';
import { useApiClientWrapper } from './apiClientWrapper';
import {
  ReceivedMessagesResponseWithDate,
  SchedulesResponseWithDate,
  SentMessagesResponseWithDate,
} from './types';

const useMessagesApi = () => {
  const credentials = useCredentials();
  const { sessionId } = useSession();
  const { hostname } = useBasePath();

  const apiClientWrapper = useApiClientWrapper();

  let basePath = getBasePath();
  let configuration: Configuration;

  if (hostname.includes('localhost')) {
    const username = credentials?.username || '';
    const password = credentials?.password || '';

    const authHeader = getAuthHeaderFromCredentials(username, password);
    configuration = new Configuration({
      basePath: `${basePath}api/config`,
      headers: {
        'X-Application-Name': 'marinepulse',
        Authorization: authHeader,
      },
    });
  } else {
    basePath = `${basePath}aisweb/api/config`;
    configuration = new Configuration({
      basePath: basePath,
      headers: {
        'Aisweb-Session': sessionId,
        'X-Application-Name': 'marinepulse',
      },
    });
  }

  const messagesApi = useMemo(() => {
    return new MessageApi(configuration);
  }, [configuration]);
  return {
    messagesApi: apiClientWrapper(messagesApi) as MessageApi,
    configuration,
  };
};

export const useMessagesReceivedList = <T = ReceivedMessagesResponseWithDate>(
  begin: Date | undefined,
  end: Date | undefined,
  options?: {
    select: (data: ReceivedMessagesResponseWithDate) => T;
  }
) => {
  const { messagesApi } = useMessagesApi();
  return useQuery({
    queryKey: ['receivedMessages', begin, end],
    queryFn: async () => {
      const result = await messagesApi.messageReceiveListGet({
        dc: '1',
        aisweb: 'true',
        begin: begin,
        end: end,
      });

      if (result) {
        convertFieldsToDates(result, [
          'begin',
          'end',
          'records.time',
          'records.formattedTime',
        ]);
      }

      return result as ReceivedMessagesResponseWithDate;
    },
    select: options?.select,
  }, 'messages:receivedList');
};

export const useMessagesSentList = <T = SentMessagesResponseWithDate>(
  begin: Date | undefined,
  end: Date | undefined,
  options?: {
    select: (data: SentMessagesResponse) => T;
  }
) => {
  const { messagesApi } = useMessagesApi();
  return useQuery({
    queryKey: ['sentMessages', begin, end],
    queryFn: async () => {
      const result = await messagesApi.messageSendListGet({
        dc: '1',
        aisweb: 'true',
        begin: begin as Date,
        end: end as Date,
      });

      if (result) {
        convertFieldsToDates(result, ['records.time', 'records.formattedTime']);
      }

      return result;
    },
    select: options?.select,
  }, 'messages:sentList');
};
export const useMessagesScheduledList = <
  T = SchedulesResponseWithDate
>(options?: {
  select: (data: SchedulesResponse) => T;
}) => {
  const { messagesApi } = useMessagesApi();
  return useQuery({
    queryKey: ['scheduledMessages'],
    queryFn: async () => {
      const result = await messagesApi.messageScheduleGet({
        dc: '1',
      });

      if (result) {
        convertFieldsToDates(result, [
          'records.schedule.end',
          'records.schedule.start',
        ]);
      }

      return result;
    },
    select: options?.select,
  }, 'messages:scheduledList');
};

export const useSendMessage = (): UseMutationResult<
  void,
  Error,
  MessageSendPostOperationRequest,
  unknown
> => {
  const { messagesApi } = useMessagesApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (message: MessageSendPostOperationRequest) => {
      return messagesApi.messageSendPost(message);
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['sentMessages'] }),
  }, 'message:send');
};

export const useScheduleMessage = (): UseMutationResult<
  void,
  Error,
  MessageSchedulePostRequest,
  unknown
> => {
  const { messagesApi } = useMessagesApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (scheduledMessage: MessageSchedulePostRequest) => {
      return messagesApi.messageSchedulePost(scheduledMessage);
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['scheduledMessages'] }),
  }, 'message:schedule');
};

export const useEditScheduleMessage = (): UseMutationResult<
  void,
  Error,
  MessageSchedulePostRequest,
  unknown
> => {
  const { messagesApi } = useMessagesApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (scheduledMessage: MessageSchedulePostRequest) => {
      return messagesApi.messageSchedulePut(scheduledMessage);
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['scheduledMessages'] }),
  }, 'message:edit_schedule');
};

export const useDeleteMessageSchedule = (): UseMutationResult<
  void,
  Error,
  string,
  unknown
> => {
  const { messagesApi } = useMessagesApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => {
      return messagesApi.messageScheduleUidDelete({ uid: id, dc: '1' });
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['scheduledMessages'] }),
  }, 'message:delete_schedule');
};
